import type { FunctionComponent } from 'react'

import { useFeatureValue } from '@growthbook/growthbook-react'

import { useGamPersonalisationInfoQuery } from '../../../generated/frontend'
import type { GeneralSize } from '../../../shared/types'
import type { Dfp, SizeMapping } from '../../hooks/useDfpSlot'
import { useDfpSlot } from '../../hooks/useDfpSlot'
import { useDismissGamPopup } from '../../hooks/useDismissGamPopup'
import type { PageInfo } from '../../types'

type DfpSlotProps = Parameters<typeof useDfpSlot>[0]

const DfpSlot: FunctionComponent<DfpSlotProps> = (props) => {
  const { id } = props
  useDfpSlot(props)
  return <div data-testid={id} id={id} className="d-flex justify-center" />
}

const PersonalisedDfpSlot: FunctionComponent<DfpSlotProps> = (props) => {
  const { data, loading } = useGamPersonalisationInfoQuery()
  const { newsletterSignups = [], newsletterRecommendation } = data?.personalisationInfo ?? {}
  const dps_013_experimentValue = useFeatureValue('DSP-013-show-gam-popups-to-members', 'OFF')
  const gbExperimentA = useFeatureValue('gam-experiment-a', 'OFF')
  const gbExperimentB = useFeatureValue('gam-experiment-b', 'OFF')
  const gbExperimentC = useFeatureValue('gam-experiment-c', 'OFF')

  if (loading) {
    return null
  }

  const dfpWithPersonalisation = {
    ...props.dfp,
    user_newsletter_signups: newsletterSignups,
    DSP_013_experiment_value: dps_013_experimentValue,
    gb_experiment_a: gbExperimentA,
    gb_experiment_b: gbExperimentB,
    gb_experiment_c: gbExperimentC,
    newsletter_recommendation:
      typeof newsletterRecommendation === 'string' ? newsletterRecommendation : undefined,
  }

  return <DfpSlot {...{ ...props, dfp: dfpWithPersonalisation }} />
}

export const GamPanel: FunctionComponent<Props> = ({
  pageInfo,
  gamId,
  ctaId,
  isOOP,
  login,
  pos,
  panelName,
  includePersonalisation,
  sizeMappings,
  slotSize,
  path,
}) => {
  useDismissGamPopup()

  if (typeof window === 'undefined') {
    return null
  }

  const { vertical, sub_vertical, category, tags, keywords, content_id, pageType } = pageInfo ?? {}

  const tagsStr = JSON.stringify(tags ?? [])
  const keywordsStr = JSON.stringify(keywords ?? [])
  const { loginStatus, transformTypeDecision } = login ?? {}
  const size: GeneralSize = slotSize ?? [600, 250]

  const dfp: Dfp = {
    vertical: vertical ?? '',
    sub_vertical: sub_vertical ?? '',
    category: category ?? '',
    tags: tagsStr,
    keywords: keywordsStr,
    login_status: loginStatus ?? 'loggedOut',
    transformTypeDecision: transformTypeDecision ?? '',
    content_id: content_id ?? '',
    page_type: pageType ?? '',
    cta_id: ctaId ?? '',
  }

  const gamPath = path
    ? path
    : dfp.vertical === 'homepage'
      ? '/30585109/cdn/homepage'
      : `/30585109/cdn/${dfp.vertical}/${dfp.sub_vertical}/${dfp.category}/${dfp.page_type}`

  if (includePersonalisation) {
    return (
      <PersonalisedDfpSlot
        {...{ dfp, size, id: gamId, path: gamPath, pageInfo, pos, panelName, sizeMappings, isOOP }}
      />
    )
  }

  return (
    <DfpSlot
      {...{ dfp, size, id: gamId, path: gamPath, pageInfo, pos, panelName, sizeMappings, isOOP }}
    />
  )
}

export type Props = {
  pageInfo: PageInfo
  gamId: string
  ctaId?: string
  isOOP?: boolean
  login?: {
    loginStatus: string
    transformTypeDecision?: string
  }
  pos?: 'pop_up' | 'acquisition_banner'
  panelName: string
  includePersonalisation?: boolean
  slotSize?: GeneralSize
  sizeMappings?: SizeMapping[]
  path?: string
}
